import { CartLine, TillProduct } from './types';

/**
 * Formats a cent amount as a euro string.
 *
 * Everything in the till is kept in whole cents right up to the moment it is shown, so
 * repeated float arithmetic can never shave a cent off a long tab.
 */
export const formatCents = (cents: number, locale = 'de-DE', currency = 'EUR'): string =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);

/**
 * A cent amount as a plain euro string for a form field — no currency symbol, no grouping.
 *
 * `''` for an unset limit, because that is what an empty input holds; anything else would
 * write a "0,00" into the field and turn "no limit" into "limit of nothing".
 */
export const centsToAmount = (cents?: number | null): string =>
    null === cents || undefined === cents ? '' : (cents / 100).toFixed(2);

/**
 * A typed euro amount back to whole cents, or null when the field was left empty.
 *
 * Accepts a comma as the decimal separator — the form is German and that is what gets
 * typed. Rounded rather than truncated so 1,999 does not quietly become 1,99.
 */
export const amountToCents = (amount?: string | number | null): number | null => {
    if (null === amount || undefined === amount || '' === amount) return null;

    const parsed = Number(String(amount).replace(',', '.').trim());

    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : null;
};

/**
 * The URL of an article's picture, or null when it has none.
 *
 * `{api}/image/{id}` is public — the kiosk runs without a session and still has to show
 * the picture, and the same route already backs the club logo and the CMS image widget.
 */
export const productImageUrl = (product: Pick<TillProduct, 'image_id'>): string | null =>
    product.image_id ? `${process.env.NEXT_PUBLIC_API_HOST}image/${product.image_id}` : null;

/** The cart's total in cents. */
export const cartTotalCents = (lines: CartLine[]): number =>
    lines.reduce((sum, line) => sum + line.product.price_cents * line.quantity, 0);

/**
 * Adds one unit of an article to the cart, or raises its quantity if it is already there.
 *
 * Tapping the same tile twice has to mean "two beers", not "two lines of one beer" — the
 * counter reads the cart back to the customer out loud.
 */
export const addToCart = (lines: CartLine[], product: TillProduct): CartLine[] => {
    // Nothing on the shelf, nothing in the cart — never a line of quantity 0. The tile is
    // disabled in this state, so this only catches a caller that is not the board.
    if (isSoldOut(product)) return lines;

    const existing = lines.find((line) => line.product.id === product.id);

    if (!existing) return [...lines, { product, quantity: 1 }];

    return lines.map((line) =>
        line.product.id === product.id ? { ...line, quantity: capToStock(product, line.quantity + 1) } : line
    );
};

/**
 * Sets an explicit quantity, dropping the line when it reaches zero.
 *
 * ⚠️ Updates only — it maps over the cart, so an article that is not in it yet stays out.
 * That is fine for the cart panel, where every row exists by definition. Anything that can
 * step up from zero needs setProductQuantity() below.
 */
export const setCartQuantity = (lines: CartLine[], productId: number, quantity: number): CartLine[] => {
    if (quantity <= 0) return lines.filter((line) => line.product.id !== productId);

    return lines.map((line) =>
        line.product.id === productId ? { ...line, quantity: capToStock(line.product, quantity) } : line
    );
};

/**
 * Sets a quantity for an article that may not be in the cart yet.
 *
 * The tile's stepper starts at zero, where there is no line to update and no product id to
 * find one by — setCartQuantity() had nothing to map over and silently did nothing, so the
 * first tap on "+" never registered. Here the product itself is in hand, so the line can be
 * created instead.
 */
export const setProductQuantity = (lines: CartLine[], product: TillProduct, quantity: number): CartLine[] => {
    const isInCart = lines.some((line) => line.product.id === product.id);

    if (isInCart || quantity <= 0) return setCartQuantity(lines, product.id, quantity);

    const capped = capToStock(product, quantity);

    // Sold out, or capped away to nothing: no line rather than a line of zero.
    return capped > 0 ? [...lines, { product, quantity: capped }] : lines;
};

/**
 * How many of an article may go into the cart.
 *
 * `Infinity` for anything the club does not count — there is no number to compare against,
 * and those articles (hours, fees) have no shelf to run out of.
 */
export const maxQuantityOf = (product: TillProduct): number =>
    product.tracks_stock ? Math.max(0, product.stock ?? 0) : Infinity;

/**
 * Clamps a requested quantity to what is actually in stock.
 *
 * ! Mirrors ShopOrderService::assertStockAvailable() on the server, which is the boundary
 * that actually holds — this only spares the operator a rejected booking. The two have to
 * keep agreeing: a till that offers 28 and a server that refuses 28 is worse than either
 * rule on its own.
 */
export const capToStock = (product: TillProduct, quantity: number): number =>
    Math.min(quantity, maxQuantityOf(product));

/** Whether an article has run out and can no longer be added. */
export const isSoldOut = (product: TillProduct): boolean => maxQuantityOf(product) <= 0;

/** Whether a cart line sits exactly at the stock ceiling, so "+" has nothing left to add. */
export const isAtStockLimit = (line: CartLine): boolean => line.quantity >= maxQuantityOf(line.product);

/**
 * The distinct categories of a product list, in tile order, with untagged articles last.
 *
 * `null` is a real bucket rather than a gap: a club that never categorised anything still
 * needs its drinks on the board.
 */
export const categoriesOf = (products: TillProduct[]): (string | null)[] => {
    const named = products.map((product) => product.category).filter((category): category is string => !!category);
    const unique = Array.from(new Set(named));
    const hasUncategorised = products.some((product) => !product.category);

    return hasUncategorised ? [...unique, null] : unique;
};

/**
 * Whether a tile may be tapped.
 *
 * `purchase_allowed === null` means no account is selected yet, so nothing is refused —
 * the board is just a catalogue at that point.
 */
export const isTileBookable = (product: TillProduct): boolean => product.purchase_allowed !== false;
